import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============================================
// Multi-Tenant Website Context Management
// ============================================

// Website ID cache - bir kez alınır, tüm uygulama boyunca kullanılır
let cachedWebsiteId: string | null = null;
let websiteIdPromise: Promise<string | null> | null = null;

// Mevcut domain'i al
export const getCurrentDomain = (): string => {
  // Localhost için test domain kullan
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return localStorage.getItem('test_website_id') || 'gulenayakademi.com';
  }
  
  // www. prefix'ini kaldır
  let domain = window.location.hostname;
  if (domain.startsWith('www.')) {
    domain = domain.substring(4);
  }
  
  return domain;
};

// Website ID'yi al (cache'li)
export const getWebsiteId = async (): Promise<string | null> => {
  // Zaten cache'de varsa direkt döndür
  if (cachedWebsiteId) {
    return cachedWebsiteId;
  }
  
  // Zaten bir istek yapılıyorsa, onu bekle
  if (websiteIdPromise) {
    return websiteIdPromise;
  }
  
  // Yeni istek başlat
  websiteIdPromise = (async () => {
    const domain = getCurrentDomain();
    cachedWebsiteId = domain;
    return cachedWebsiteId;
  })();
  
  return websiteIdPromise;
};

// Senkron website ID (cache'den) - sadece cache dolduktan sonra kullan
export const getWebsiteIdSync = (): string | null => {
  return cachedWebsiteId;
};

// Website ID'yi manuel set et (test için)
export const setWebsiteId = (id: string): void => {
  cachedWebsiteId = id;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    localStorage.setItem('test_website_id', id);
  }
};

// ============================================
// Database Query Helpers
// ============================================

// SELECT sorgusu için website_id filtreli helper
export const queryWithWebsite = async <T>(
  tableName: string,
  queryBuilder?: (query: ReturnType<typeof supabase.from>) => ReturnType<typeof supabase.from>
): Promise<{ data: T[] | null; error: Error | null }> => {
  const websiteId = await getWebsiteId();
  
  let query = supabase.from(tableName).select('*').eq('website_id', websiteId);
  
  if (queryBuilder) {
    query = queryBuilder(query as any) as any;
  }
  
  const { data, error } = await query;
  return { data: data as T[] | null, error };
};

// INSERT için website_id ekleyen helper
export const insertWithWebsite = async <T>(
  tableName: string,
  data: Partial<T> | Partial<T>[]
): Promise<{ data: T[] | null; error: Error | null }> => {
  const websiteId = await getWebsiteId();
  
  const dataWithWebsite = Array.isArray(data)
    ? data.map(item => ({ ...item, website_id: websiteId }))
    : { ...data, website_id: websiteId };
  
  const { data: result, error } = await supabase
    .from(tableName)
    .insert(dataWithWebsite as any)
    .select();
  
  return { data: result as T[] | null, error };
};

// UPDATE için helper (website_id kontrolü ile)
export const updateWithWebsite = async <T>(
  tableName: string,
  id: string,
  data: Partial<T>
): Promise<{ data: T[] | null; error: Error | null }> => {
  const websiteId = await getWebsiteId();
  
  const { data: result, error } = await supabase
    .from(tableName)
    .update(data as any)
    .eq('id', id)
    .eq('website_id', websiteId)
    .select();
  
  return { data: result as T[] | null, error };
};

// DELETE için helper (website_id kontrolü ile)
export const deleteWithWebsite = async (
  tableName: string,
  id: string
): Promise<{ error: Error | null }> => {
  const websiteId = await getWebsiteId();
  
  const { error } = await supabase
    .from(tableName)
    .delete()
    .eq('id', id)
    .eq('website_id', websiteId);
  
  return { error };
};

// UPSERT için helper
export const upsertWithWebsite = async <T>(
  tableName: string,
  data: Partial<T>,
  conflictColumn: string = 'id'
): Promise<{ data: T[] | null; error: Error | null }> => {
  const websiteId = await getWebsiteId();
  
  const { data: result, error } = await supabase
    .from(tableName)
    .upsert({ ...data, website_id: websiteId } as any, { onConflict: conflictColumn })
    .select();
  
  return { data: result as T[] | null, error };
};

// ============================================
// Type Definitions
// ============================================

export interface Teacher {
  id: string;
  name: string;
  title: string;
  bio: string;
  image_url: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface Slider {
  id: string;
  title: string;
  subtitle: string;
  image_url: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ThemeSettings {
  id: string;
  site_title?: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  text_color: string;
  background_color: string;
  created_at?: string;
  updated_at?: string;
  phone?: string;
  email?: string;
  address?: string;
  working_hours?: string;
  map_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  navbar_style?: 'transparent' | 'solid';
  navbar_bg_color?: string;
  navbar_text_color?: string;
  logo_url?: string;
  button_text_color?: string;
  logo_text?: string;
  show_logo_text?: boolean;
  logo_width?: number;
  logo_height?: number;
  logo_padding_x?: number;
  logo_padding_y?: number;
  logo_bg_style?: 'white' | 'transparent';
  footer_bg_color?: string;
  navbar_position?: 'top' | 'left' | 'right';
  navbar_display_mode?: 'icon' | 'icon_text';
  navbar_width?: 'full' | 'boxed';
  topbar_enabled?: boolean;
  topbar_bg_color?: string;
  topbar_text_color?: string;
  topbar_left_type?: 'none' | 'phone' | 'email' | 'text';
  topbar_left_text?: string;
  topbar_center_type?: 'none' | 'phone' | 'email' | 'text';
  topbar_center_text?: string;
  topbar_right_type?: 'none' | 'phone' | 'email' | 'social' | 'text';
  topbar_right_text?: string;
  topbar_hide_on_scroll?: boolean;
}

export interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  parent_id: string | null;
  order_index: number;
  is_visible: boolean;
  is_active: boolean;
  target: string;
  created_at: string;
  updated_at: string;
}
